import { AxiosInstance } from "axios";
import _ from "lodash";
import QueryString from "qs";
import { ApiRouteHelpers } from "../../../root/apiRoutes";
import { RegulationRequestTypeDto } from "../dto/RegulationRequestTypeDto";
import {
  DataSubjectRequestDetailDto,
  DataSubjectRequestSummaryDto,
  OrganizationDataSubjectRequestBatchesDto,
  PaginatedSubjectRequestDto,
  SubjectStatus,
} from "../dto/DataSubjectRequestDto";
import { Filter } from "../dto/FilterDto";
import { OkReturnDto } from "../dto/OkReturnDto";
import { SortRule } from "../dto/SortRuleDto";
import { ContactVendorRequest, UpdateVendorRequest } from "../dto/UpdateVendorRequest";
import {
  DataRequestFilter,
  DataRequestPublicId,
  DataRequestState,
  DataRequestSubstate,
  Iso8601Date,
  OrganizationPublicId,
  RequestTypeFilter,
  UUIDString,
} from "../Types";
import { ConsumerDetailPageParams } from "../../../../polaris/pages/consumer/ConsumerDetailPage";
import { TextContentDto } from "../dto/TextContentDto";

export type CreateDataRequest = {
  firstName: string;
  lastName: string;
  email: string;
  requestType: string; // Simple string like "PORTABILITY", "DELETE", etc.
  requestCountryRegionCode?: string; // Region code like "US-CA", "EU"
  phoneNumber?: string;
  mailingAddress?: string;
  organizationCustomInput?: string;
  confirmation?: boolean;
  descriptionInput?: string;
  appealId?: string;
  withdrawnConsent?: string[];
  notes?: string;
  gaUserId?: string;
  subjectCustomFields?: Record<string, string>;
  context?: string; // "CONSUMER" or "EMPLOYEE"
  requestDate?: string;
  skipEmailVerification?: boolean;
};

export type TransitionDataRequest = {
  state: DataRequestState;
  substate?: DataRequestSubstate;
  notes?: string;
  attachment?: File;
};

export type VerifyDataRequest = {
  consumerGroupId: UUIDString;
  notes?: string;
};

export type MessageDto = {
  message: string;
  attachments?: FileList[];
};

export type ContactRequestorOptions = {
  attachments?: File[];
  closeAfterSend?: boolean;
  currentRequestState?: DataRequestState;
};

export type UpdateRequest = {
  requestDate: Iso8601Date;
  subjectEmailAddress?: string;
  subjectPhoneNumber?: string;
  subjectFreePhone?: string;
  subjectMailingAddress?: string;
  subjectFreeTextAddress?: string;
  subjectOnlineIdentifiers?: string;
  contactVendorsHidden?: boolean;
  subjectFirstName: string;
  subjectLastName: string;
  submissionMethod: string;
  dataRequestType: RegulationRequestTypeDto;
  appealResolutionDescription?: string;
  subjectCustomFields?: Record<string, string>;
  correctionFinished?: boolean;
  nothingToCorrect?: boolean;
};

export type extendRequest = {
  notice: string;
};

export type StepConsumerGroupUpdate = {
  subjectStatus: SubjectStatus;
  consumerGroups?: string[];
};

export const filtersToParams = (filters: Filter[]) =>
  filters
    .filter((f) => Boolean(f.value))
    .reduce<Record<any, string | number>>((acc, f) => {
      acc ||= {};
      // we have filtered out `undefined` in the above filter
      acc[f.id] = f.value as string | number;
      return acc;
    }, {});

export class DataRequestController {
  constructor(private axios: AxiosInstance) {}

  public getRequests = async (
    organizationId: OrganizationPublicId,
    filter?: DataRequestFilter,
    filters?: Filter[],
    sortBy?: SortRule[],
    type: RequestTypeFilter = "all",
    globalSearchTerm?: string,
    page = 0,
    per = 15,
  ): Promise<PaginatedSubjectRequestDto> => {
    const url = ApiRouteHelpers.organizationRequests(organizationId);
    let params = { filter, type, globalSearchTerm, page, per };

    if (filters) {
      const filterParams = filtersToParams(filters);
      params = Object.assign({}, params, filterParams);
    }

    params["sort"] = _.get(sortBy, "0.id");
    params["desc"] = _.get(sortBy, "0.desc");

    const response = await this.axios.get<PaginatedSubjectRequestDto>(url, {
      params,
    });
    return response.data as PaginatedSubjectRequestDto;
  };

  public getStats = async (
    organizationId: OrganizationPublicId,
  ): Promise<DataSubjectRequestSummaryDto> => {
    const url = ApiRouteHelpers.organizationRequestStats(organizationId);
    const response = await this.axios.get<DataSubjectRequestSummaryDto>(url);

    return response.data as DataSubjectRequestSummaryDto;
  };

  public getRequestsForConsumer = async (
    organizationId: OrganizationPublicId,
    consumerDetails: ConsumerDetailPageParams,
  ): Promise<DataSubjectRequestDetailDto[]> => {
    const url = ApiRouteHelpers.consumer.requests(organizationId);
    const response = await this.axios.get<DataSubjectRequestDetailDto[]>(url, {
      params: consumerDetails,
      paramsSerializer: QueryString.stringify,
    });

    return response.data as DataSubjectRequestDetailDto[];
  };

  public getRequest = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequest(organizationId, requestId);
    const response = await this.axios.get<DataSubjectRequestDetailDto>(url);

    return response.data as DataSubjectRequestDetailDto;
  };

  public getRequestClosedTemplate = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
  ): Promise<string> => {
    const url = ApiRouteHelpers.organizationRequestClosedTemplate(organizationId, requestId);
    const response = await this.axios.get<TextContentDto>(url);
    return response.data.content;
  };

  public getRequestContactVendorsMessageTemplate = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
  ): Promise<string> => {
    const url = ApiRouteHelpers.organizationRequestContactVendorsMessageTemplate(
      organizationId,
      requestId,
    );
    const response = await this.axios.get<TextContentDto>(url);
    return response.data.content;
  };

  public getRequestContactVendorsPreviewMessageTemplateForRequestType = async (
    organizationId: OrganizationPublicId,
    requestType: "delete" | "opt-out" | "limit",
  ): Promise<string> => {
    const url = ApiRouteHelpers.organizationRequestContactVendorsPreviewMessage(
      organizationId,
      requestType,
    );
    const response = await this.axios.get<TextContentDto>(url);
    return response.data.content;
  };

  public updateRequest = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    updateRequest: UpdateRequest,
  ): Promise<DataSubjectRequestSummaryDto> => {
    const url = ApiRouteHelpers.organizationRequest(organizationId, requestId);
    const response = await this.axios.put<DataSubjectRequestSummaryDto>(url, updateRequest);
    return response.data as DataSubjectRequestSummaryDto;
  };

  public extendRequest = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    extendRequest: extendRequest,
  ): Promise<DataSubjectRequestSummaryDto> => {
    const url = ApiRouteHelpers.organizationRequestExtend(organizationId, requestId);
    const response = await this.axios.put<DataSubjectRequestSummaryDto>(url, extendRequest);
    return response.data as DataSubjectRequestSummaryDto;
  };

  public createRequest = async (
    organizationId: OrganizationPublicId,
    createRequest: CreateDataRequest,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequests(organizationId);
    const response = await this.axios.post<DataSubjectRequestDetailDto>(url, createRequest);
    return response.data as DataSubjectRequestDetailDto;
  };

  public removeRequest = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
  ): Promise<OkReturnDto> => {
    const url = ApiRouteHelpers.organizationRequest(organizationId, requestId);
    const response = await this.axios.delete(url);
    return response.data as OkReturnDto;
  };

  public transitionRequest = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    transitionRequest: TransitionDataRequest,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequestState(organizationId, requestId);

    const formData = new FormData();

    if (transitionRequest.state) {
      formData.append("state", transitionRequest.state);
    }
    if (transitionRequest.substate) {
      formData.append("substate", transitionRequest.substate);
    }
    if (transitionRequest.notes) {
      formData.append("notes", transitionRequest.notes);
    }
    if (transitionRequest.attachment) {
      formData.append("attachment", transitionRequest.attachment);
    }

    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };

    const response = await this.axios.put<DataSubjectRequestDetailDto>(url, formData, config);
    return response.data as DataSubjectRequestDetailDto;
  };

  public verifyRequest = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    verifyRequest: VerifyDataRequest,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequestVerify(organizationId, requestId);
    const response = await this.axios.post<DataSubjectRequestDetailDto>(url, verifyRequest);
    return response.data as DataSubjectRequestDetailDto;
  };

  public getBatches = async (
    organizationId: OrganizationPublicId,
  ): Promise<OrganizationDataSubjectRequestBatchesDto> => {
    const url = ApiRouteHelpers.organizationRequestBatches(organizationId);
    const response = await this.axios.get<OrganizationDataSubjectRequestBatchesDto>(url);
    return response.data as OrganizationDataSubjectRequestBatchesDto;
  };

  public updateVendor = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    updateVendorRequest: UpdateVendorRequest,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequestVendor(organizationId, requestId);
    const response = await this.axios.put<DataSubjectRequestDetailDto>(url, updateVendorRequest);
    return response.data as DataSubjectRequestDetailDto;
  };

  public autodeleteGoogleAnalytics = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
  ) => {
    const url = ApiRouteHelpers.organizationRequestAutodeleteGoogleAnalytics(
      organizationId,
      requestId,
    );
    await this.axios.post(url);
  };

  public contactVendors = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    updateVendorRequest: ContactVendorRequest,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequestContactVendors(organizationId, requestId);
    const response = await this.axios.put<DataSubjectRequestDetailDto>(url, updateVendorRequest);
    return response.data as DataSubjectRequestDetailDto;
  };

  public contactRequestor = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    options: ContactRequestorOptions = {},
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequestContactRequestor(organizationId, requestId);

    const formData = new FormData();

    const attachments = options.attachments ?? [];

    if (attachments.length > 0) {
      attachments.forEach((file) => formData.append("attachment", file));
    }

    if (options.closeAfterSend !== undefined) {
      formData.append("closeAfterSend", String(options.closeAfterSend));
    }

    if (options.currentRequestState) {
      formData.append("currentRequestState", options.currentRequestState);
    }

    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };

    const response = await this.axios.put<DataSubjectRequestDetailDto>(url, formData, config);
    return response.data as DataSubjectRequestDetailDto;
  };

  public createNote = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    note: MessageDto,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequestNote(organizationId, requestId);
    const formData = new FormData();
    formData.append("message", note.message);
    if (note.attachments) {
      // attachments is an array containing one FileList, containing the Files to be attached
      Array.from(note.attachments[0]).forEach((f) => {
        formData.append("attachments[]", f);
      });
    }

    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };

    const response = await this.axios.post<DataSubjectRequestDetailDto>(url, formData, config);
    return response.data as DataSubjectRequestDetailDto;
  };

  public reopen = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequestReopen(organizationId, requestId);
    const response = await this.axios.put<DataSubjectRequestDetailDto>(url);
    return response.data as DataSubjectRequestDetailDto;
  };

  public stepConsumerGroup = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    payload: StepConsumerGroupUpdate,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequestStepConsumerGroup(organizationId, requestId);
    const response = await this.axios.put<DataSubjectRequestDetailDto>(url, payload);
    return response.data as DataSubjectRequestDetailDto;
  };

  public setSubjectStatus = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    payload: StepConsumerGroupUpdate,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequestSetSubjectStatus(organizationId, requestId);
    const response = await this.axios.put<DataSubjectRequestDetailDto>(url, payload);
    return response.data as DataSubjectRequestDetailDto;
  };

  public stepRequestForward = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    currentRequestState: DataRequestState,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequestStepForward(organizationId, requestId);
    const response = await this.axios.get<DataSubjectRequestDetailDto>(url, {
      params: { currentRequestState },
    });
    return response.data as DataSubjectRequestDetailDto;
  };

  public stepRequestBack = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    currentRequestState: DataRequestState,
  ): Promise<DataSubjectRequestDetailDto> => {
    const url = ApiRouteHelpers.organizationRequestStepBack(organizationId, requestId);
    const response = await this.axios.get<DataSubjectRequestDetailDto>(url, {
      params: { currentRequestState },
    });
    return response.data as DataSubjectRequestDetailDto;
  };

  public manuallyAutomateRequest = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
  ): Promise<{
    success: boolean;
    message?: string;
    errors?: string[];
  }> => {
    const url = ApiRouteHelpers.organizationRequestManuallyAutomate(organizationId, requestId);
    const response = await this.axios.post<{
      success: boolean;
      message?: string;
      errors?: string[];
    }>(url);
    return response.data;
  };

  public uploadAttachment = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    file: File,
  ): Promise<any> => {
    const url = ApiRouteHelpers.organizationRequestAttachments(organizationId, requestId);
    const formData = new FormData();
    formData.append("file", file);

    const response = await this.axios.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  };

  public getAttachments = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
  ): Promise<any[]> => {
    const url = ApiRouteHelpers.organizationRequestAttachments(organizationId, requestId);
    const response = await this.axios.get(url);
    return response.data;
  };

  public deleteAttachment = async (
    organizationId: OrganizationPublicId,
    requestId: DataRequestPublicId,
    attachmentId: string,
  ): Promise<void> => {
    const url = ApiRouteHelpers.organizationRequestAttachment(
      organizationId,
      requestId,
      attachmentId,
    );
    await this.axios.delete(url);
  };
}
